import { Type, destroy, read, write } from '@acorns/web-utils'
import type ApolloClient from 'apollo-client'
import { debounce } from 'lodash'

import logoutMutation from '../graphql-operations/mutations/session/logout.gql'
import refreshAuthTokensMutation from '../graphql-operations/mutations/session/refreshAuthTokens.gql'

const SESSION_LAST_REFRESHED_SESSION_STORAGE_KEY = 'session_last_refreshed'

export const SESSION_REFRESH_QUEUED_SESSION_STORAGE_KEY =
  'session_refresh_queued'

export const SESSION_INACTIVITY_DETECTED_SESSION_STORAGE_KEY =
  'session_inactivity_detected'

// Optimistically reads token from `acornsweb_token` cookie. Falls back to legacy local storage implementation
export const resolveTokenFromStorage = () =>
  read(Type.token) || getStorageValue(Type.token)

const storageKey = (type: Type): string => {
  return `acorns_web_${type}`
}

const getStorageValue = (type: Type) => {
  return sessionStorage.getItem(storageKey(type)) || undefined
}

const setStorageValue = (type: Type, value: string) => {
  sessionStorage.setItem(storageKey(type), value)
}

const clearStorageValue = (type: Type) => {
  sessionStorage.removeItem(storageKey(type))
}

// Rewrites cookie for the purpose of extending max-age
const refreshCookie = (type: Type) => {
  const cookieValue = read(type)

  let value = cookieValue

  if (cookieValue) {
    setStorageValue(type, cookieValue)
  } else {
    value = getStorageValue(type)
  }

  if (value) {
    write(type, value)
  }
}

const isImplicitCookieAuth = () => {
  return !!read(Type.csrfToken)
}

const isJwtAuth = () => {
  const token = read(Type.token)
  return (token && token.length > 64) || isImplicitCookieAuth()
}

const refreshJwtAuthCookies = async (apolloClient: ApolloClient<object>) => {
  const refreshToken = isImplicitCookieAuth() ? '' : read(Type.refreshToken)
  const response = await apolloClient.mutate({
    mutation: refreshAuthTokensMutation,
    variables: {
      // IMPORTANT:
      // For "implicit cookie auth", the GraphQL gateway will replace the value for the `refreshToken` variable
      // with the token value retrieved from the HttpOnly cookie storing the refresh token.
      // However, (as a hack) a "dummy" variable with the name `refreshToken` NEEDS to be defined for this to work properly.
      refreshToken,
    },
  })

  if (!isImplicitCookieAuth() && response.data) {
    // This is for local development only, which doesn't use implicit cookie auth.
    const { refreshAuthToken } = response.data
    write(Type.token, refreshAuthToken.token)
    write(Type.refreshToken, refreshAuthToken.refreshToken)
  }
}

const refreshLegacyAuthCookies = () => {
  refreshCookie(Type.token)
}

const updateSessionLastRefreshedTimestamp = () => {
  window.sessionStorage.setItem(
    SESSION_LAST_REFRESHED_SESSION_STORAGE_KEY,
    Date.now().toString(),
  )
}

const clearSessionLastRefreshedTimestamp = () => {
  window.sessionStorage.removeItem(SESSION_LAST_REFRESHED_SESSION_STORAGE_KEY)
}

export const isAuthenticated = () => {
  return Boolean(read(Type.token)) || Boolean(read(Type.csrfToken))
}

export const hasSessionLastRefreshedTimestamp = () => {
  return Boolean(
    window.sessionStorage.getItem(SESSION_LAST_REFRESHED_SESSION_STORAGE_KEY),
  )
}

export const getTimeElapsedSinceLastSessionRefresh = () => {
  return (
    Date.now() -
    parseInt(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- if this session storage item doesn't exist when this is called, there's a bug
      window.sessionStorage.getItem(
        SESSION_LAST_REFRESHED_SESSION_STORAGE_KEY,
      )!,
      10,
    )
  )
}

export const refreshSessionCookies = async (
  apolloClient: ApolloClient<object>,
) => {
  if (isJwtAuth()) {
    await refreshJwtAuthCookies(apolloClient)
  } else {
    refreshLegacyAuthCookies()
  }

  refreshCookie(Type.partition)
  updateSessionLastRefreshedTimestamp()

  window.sessionStorage.removeItem(SESSION_REFRESH_QUEUED_SESSION_STORAGE_KEY)
}

export const debouncedRefreshSessionCookies = debounce(
  refreshSessionCookies,
  5000, // 5 seconds
)

// Destroys all `acornsweb_` cookies
export const logout = async (apolloClient: ApolloClient<object>) => {
  // avoid calling the `logout` mutation unnecessarily; call it only when we know we have auth tokens to expire
  const hasAuthTokens = read(Type.csrfToken)
  if (hasAuthTokens) {
    try {
      await apolloClient.mutate({
        mutation: logoutMutation,
      })
    } catch {
      // intentionally do nothing;
      // even an error response is expected to contain the necessary HTTP headers to delete/expire the auth-related cookies
    } finally {
      destroy(Type.csrfToken)
    }
  }

  destroy(Type.token)
  destroy(Type.partition)
  clearStorageValue(Type.token)
  clearStorageValue(Type.partition)
  clearSessionLastRefreshedTimestamp()

  // destroying the refresh token cookie is only relevant for the developer-only login
  destroy(Type.refreshToken)
}
